import {ariaLabelDate} from '@github-ui/aria-label-date'
import {isCopilot} from '@github-ui/assignees/copilot-user'
import {isLoggedIn} from '@github-ui/client-env'
import {copyText} from '@github-ui/copy-to-clipboard'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {CreateIssueDialogEntry, type CreateIssueDialogEntryProps} from '@github-ui/issue-create/CreateIssueDialogEntry'
import {DisplayMode} from '@github-ui/issue-create/DisplayMode'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {useAnalytics} from '@github-ui/use-analytics'
import {
  ArrowUpRightIcon,
  BlockedIcon,
  CopilotIcon,
  EyeClosedIcon,
  EyeIcon,
  FoldIcon,
  IssueOpenedIcon,
  KebabHorizontalIcon,
  LinkIcon,
  MarkdownIcon,
  PencilIcon,
  PinIcon,
  PinSlashIcon,
  QuoteIcon,
  ReportIcon,
  TrashIcon,
  UnfoldIcon,
} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton} from '@primer/react'
import type {SxProp} from '@primer/styled-react'
import {useCallback, useMemo, useState} from 'react'

import {TEST_IDS} from '../constants/test-ids'
import {useUpdateIssueFromCommentWithCopilot} from '../hooks/use-update-issue-from-comment-with-copilot'
import {canBlock, canUnblock} from '../utils/comment-actions'
import {type SelectionContext, selectQuoteFromComment} from '../utils/quotes'
import styles from './CommentActions.module.css'
import type {CommentAuthorAssociation as GraphQlCommentAuthorAssociation} from './issue-comment/__generated__/IssueCommentHeader.graphql'
import {BlockUserFromOrgDialog, type BlockUserFromOrgDialogProps} from './issue-comment/BlockUserFromOrgDialog'
import {type HIDE_OPTIONS, HideCommentActionItems} from './issue-comment/HideCommentActions'
import {ReportContentDialog} from './issue-comment/ReportContentDialog'
import {UnblockUserFromOrgDialog, type UnblockUserFromOrgDialogProps} from './issue-comment/UnblockUserFromOrgDialog'

export interface CommentData {
  authorAssociation: GraphQlCommentAuthorAssociation
  body: string
  bodyForClipboard?: string
  id: string
  createdAt: string
  isHidden: boolean
  referenceText: string
  minimizedReason: string | null | undefined
  pendingMinimizeReason?: string | null | undefined
  pendingBlock?: boolean | null
  pendingUnblock?: boolean | null
  repository: {
    id: string
    isPrivate: boolean
    name: string
    owner: {
      id: string
      login: string
      url: string
    }
  }
  url: string
  createdViaEmail?: boolean
  viaApp?: {
    name: string
    url: string
  }
  viewerCanDelete: boolean
  viewerCanMinimize: boolean
  viewerCanUnminimize: boolean
  viewerCanPin?: boolean
  viewerCanUnpin?: boolean
  viewerCanSeeMinimizeButton: boolean
  viewerCanSeeUnminimizeButton: boolean
  viewerCanUpdate: boolean
  viewerCanReport: boolean
  viewerCanReportToMaintainer: boolean
  viewerCanBlockFromOrg: boolean
  viewerCanUnblockFromOrg: boolean
  viewerCanReferenceInIssue?: boolean
  viewerCanQuoteReply?: boolean
  author:
    | {
        id: string
        login: string
      }
    | null
    | undefined
  authorToRepoOwnerSponsorship?: {
    createdAt: string
    isActive: boolean
  } | null
  __id?: string
  pinnedBy?: {
    login: string
  } | null
}

export type CommentActionsProps = {
  comment: CommentData
  commentAuthorLogin: string
  editComment: () => void
  onReplySelect: (quotedText?: string) => void
  isMinimized: boolean
  isReply?: boolean
  navigate: (url: string) => void
  onBlock?: BlockUserFromOrgDialogProps['onBlock']
  onUnblock?: UnblockUserFromOrgDialogProps['onUnblock']
  onMinimize?: (value: boolean) => void
  hideComment: (reason: HIDE_OPTIONS) => void
  unhideComment: () => void
  deleteComment: () => void
  onSuccessfulBlock?: () => void
  commentRef?: React.RefObject<HTMLDivElement | null>
  isDisabled?: boolean
  showCopilotMenuItem?: boolean
  pinComment?: (commentId: string) => void
  unpinComment?: (commentId: string) => void
} & SxProp

export function CommentActions({
  comment,
  commentAuthorLogin,
  editComment,
  onReplySelect,
  isMinimized = false,
  isReply = false,
  navigate,
  onBlock,
  onUnblock,
  onMinimize: onMinimized,
  hideComment,
  unhideComment,
  deleteComment,
  onSuccessfulBlock,
  commentRef,
  isDisabled = false,
  showCopilotMenuItem = false,
  pinComment,
  unpinComment,
}: CommentActionsProps) {
  const [menuOpened, setMenuOpened] = useState(false)
  const [selection, setSelection] = useState<SelectionContext | null>(null)
  const [isNewIssueOpen, setIsNewIssueOpen] = useState(false)
  const [isReportContentOpen, setIsReportContentOpen] = useState(false)
  const [isBlockUserFromOrgOpen, setIsBlockUserFromOrgOpen] = useState(false)
  const [isUnblockUserFromOrgOpen, setIsUnblockUserFromOrgOpen] = useState(false)
  const {updateIssueWithCopilot} = useUpdateIssueFromCommentWithCopilot()
  const {sendAnalyticsEvent} = useAnalytics()

  // Check if the specific comment can be updated and if the prop allows showing the menu
  const shouldShowCopilotMenuItem = showCopilotMenuItem && comment.viewerCanUpdate

  const formatBodyAsReference = useCallback(
    (str: string) => {
      return `${str.replace(/^(?!>)/gm, '> ')} \n\n _Originally posted by @${commentAuthorLogin} in [${
        comment.referenceText
      }](${comment.url})_`
    },
    [comment.referenceText, commentAuthorLogin, comment.url],
  )

  const copyToClipboard = (textToCopy: string) => {
    copyText(textToCopy)
  }

  const onUpdateIssue = useCallback(() => {
    updateIssueWithCopilot(comment)
  }, [updateIssueWithCopilot, comment])

  const commentHidden = !!comment.pendingMinimizeReason || comment.isHidden

  const minimizeButton = useMemo(() => {
    return !commentHidden || isMinimized || !comment.viewerCanSeeMinimizeButton ? null : (
      <IconButton
        aria-label={'hide comment'}
        variant={'invisible'}
        onClick={() => onMinimized?.(true)}
        icon={FoldIcon}
        size={'small'}
        className={styles.CommentActionsIconButton}
      />
    )
  }, [commentHidden, isMinimized, onMinimized, comment.viewerCanSeeMinimizeButton])

  const unminimizeButton = useMemo(() => {
    return !commentHidden || !isMinimized || !comment.viewerCanSeeUnminimizeButton ? null : (
      <IconButton
        aria-label={'show comment'}
        variant={'invisible'}
        onClick={() => onMinimized?.(false)}
        icon={UnfoldIcon}
        size={'small'}
        className={styles.CommentActionsIconButton}
      />
    )
  }, [commentHidden, isMinimized, onMinimized, comment.viewerCanSeeUnminimizeButton])

  const reportUrl = useMemo(() => {
    if (!comment.author) return undefined
    const host = ssrSafeWindow?.location.origin
    if (!host) return undefined
    const query = `content_url=${encodeURIComponent(comment.url)}&report=${commentAuthorLogin}+(user)`
    const url = `${host}/contact/report-content?${query}`

    return url
  }, [commentAuthorLogin, comment.author, comment.url])

  const showReportSection = (comment.viewerCanReport || comment.viewerCanReportToMaintainer) && comment.author

  const isAuthorCopilot = isCopilot(commentAuthorLogin)

  const userCanBlock = canBlock({
    viewerCanBlockFromOrg: comment.viewerCanBlockFromOrg,
    pendingBlock: !!comment.pendingBlock,
    pendingUnblock: !!comment.pendingUnblock,
    hasAuthor: !!comment.author,
    isAuthorCopilot,
  })

  const userCanUnblock = canUnblock({
    viewerCanUnblockFromOrg: comment.viewerCanUnblockFromOrg,
    pendingBlock: !!comment.pendingBlock,
    pendingUnblock: !!comment.pendingUnblock,
    hasAuthor: !!comment.author,
  })

  // Default value for reference in issue creation is true
  const userCanReferenceInIssue =
    typeof comment.viewerCanReferenceInIssue === 'boolean' ? comment.viewerCanReferenceInIssue : true

  // Default value for quoting in reply is true
  const userCanQuoteReply = typeof comment.viewerCanQuoteReply === 'boolean' ? comment.viewerCanQuoteReply : true

  const isPinned = Boolean(comment.pinnedBy)
  const userCanPin = comment.viewerCanPin && !isPinned && pinComment !== undefined
  const userCanUnpin = comment.viewerCanUnpin && isPinned && unpinComment !== undefined

  const handlePin = () => {
    pinComment?.(comment.id)
  }
  const handleUnpin = () => {
    unpinComment?.(comment.id)
  }

  const issueCreateProps: CreateIssueDialogEntryProps = {
    isCreateDialogOpen: isNewIssueOpen,
    setIsCreateDialogOpen: setIsNewIssueOpen,
    navigate,
    optionConfig: {
      storageKeyPrefix: comment.id,
      defaultDisplayMode: DisplayMode.IssueCreation,
      scopedOrganization: comment.repository.owner.login,
      issueCreateArguments: {
        initialValues: {
          body: formatBodyAsReference(comment.body),
        },
        repository: {
          name: comment.repository.name,
          owner: comment.repository.owner.login,
        },
      },
    },
  }
  const createdAtFormatted = ariaLabelDate(new Date(comment.createdAt))
  const commentActionsLabel = `Actions for ${commentAuthorLogin}'s ${
    isReply ? 'reply' : 'comment'
  }, ${createdAtFormatted}`

  return (
    <>
      {isNewIssueOpen && <CreateIssueDialogEntry {...issueCreateProps} />}
      {isReportContentOpen && (
        <ReportContentDialog
          owner={comment.repository.owner.login}
          ownerUrl={comment.repository.owner.url}
          reportUrl={reportUrl}
          contentId={comment.id}
          onClose={() => setIsReportContentOpen(false)}
          contentType={'comment'}
        />
      )}
      {isBlockUserFromOrgOpen && comment.author && (
        <BlockUserFromOrgDialog
          onBlock={onBlock}
          onClose={() => {
            onSuccessfulBlock?.()
            setIsBlockUserFromOrgOpen(false)
          }}
          organization={comment.repository.owner}
          contentId={comment.id}
          contentAuthor={comment.author}
          contentUrl={comment.url}
        />
      )}
      {isUnblockUserFromOrgOpen && comment.author && (
        <UnblockUserFromOrgDialog
          onClose={() => setIsUnblockUserFromOrgOpen(false)}
          onUnblock={onUnblock}
          organization={comment.repository.owner}
          contentAuthor={comment.author}
          contentId={comment.id}
        />
      )}
      {minimizeButton}
      {unminimizeButton}
      <ActionMenu
        open={menuOpened}
        onOpenChange={(value: boolean) => {
          if (value) {
            const current = window.getSelection()
            if (current && current.anchorNode) {
              setSelection({
                anchorNode: current.anchorNode,
                range: current.getRangeAt(0),
              })
            } else {
              setSelection(null)
            }

            // Send analytics event when menu transitions from closed to open
            if (!menuOpened && isFeatureEnabled('copilot_comment_update_issue_body') && shouldShowCopilotMenuItem) {
              sendAnalyticsEvent('analytics.click', 'COMMENT_ACTIONS_MENU_OPENED', {
                commentId: comment.id,
              })
            }
          }
          setMenuOpened(value)
        }}
      >
        <ActionMenu.Anchor data-testid={TEST_IDS.commentHeaderHamburger}>
          <IconButton
            disabled={isDisabled}
            size="small"
            icon={KebabHorizontalIcon}
            variant="invisible"
            aria-label={commentActionsLabel}
            tooltipDirection="n"
            description="More actions"
            className={styles.CommentActionsIconButton}
          />
        </ActionMenu.Anchor>
        <ActionMenu.Overlay>
          <ActionList data-testid={TEST_IDS.commentHeaderHamburgerOpen}>
            <ActionList.Item
              inactiveText={comment.url === '' ? 'Comment must have a URL to be copied' : undefined}
              onSelect={() => copyToClipboard(comment.url)}
            >
              <ActionList.LeadingVisual>
                <LinkIcon />
              </ActionList.LeadingVisual>
              Copy link
            </ActionList.Item>
            {isFeatureEnabled('comment_viewer_copy_raw_markdown') ? (
              <ActionList.Item
                inactiveText={comment.body === '' ? 'Comment must have content to be copied' : undefined}
                onSelect={() => copyToClipboard(comment.bodyForClipboard || comment.body)}
              >
                <ActionList.LeadingVisual>
                  <MarkdownIcon />
                </ActionList.LeadingVisual>
                Copy Markdown
              </ActionList.Item>
            ) : null}
            {isLoggedIn() && (
              <>
                {userCanQuoteReply && (
                  <ActionList.Item
                    onSelect={() => {
                      const newValue = selectQuoteFromComment(commentRef?.current, selection, comment.body)
                      onReplySelect(newValue)
                    }}
                  >
                    <ActionList.LeadingVisual>
                      <QuoteIcon />
                    </ActionList.LeadingVisual>
                    Quote reply
                  </ActionList.Item>
                )}
                {userCanReferenceInIssue && (
                  <>
                    <ActionList.Divider />
                    <ActionList.Item
                      data-testid={TEST_IDS.commentMenuRefComment}
                      onSelect={() => setIsNewIssueOpen(true)}
                      className={styles.CommentActionsListItem}
                    >
                      <ActionList.LeadingVisual>
                        <IssueOpenedIcon />
                      </ActionList.LeadingVisual>
                      Reference in a new issue
                    </ActionList.Item>
                    {isFeatureEnabled('copilot_comment_update_issue_body') && shouldShowCopilotMenuItem && (
                      <ActionList.Item onSelect={onUpdateIssue}>
                        <ActionList.LeadingVisual>
                          <CopilotIcon />
                        </ActionList.LeadingVisual>
                        Update issue
                        <ActionList.TrailingVisual>
                          <ArrowUpRightIcon />
                        </ActionList.TrailingVisual>
                      </ActionList.Item>
                    )}
                  </>
                )}
              </>
            )}
            {(comment.viewerCanUpdate ||
              comment.viewerCanMinimize ||
              comment.viewerCanPin ||
              comment.viewerCanUnpin ||
              comment.viewerCanDelete) && <ActionList.Divider />}
            {comment.viewerCanUpdate && (
              <ActionList.Item onSelect={() => editComment()}>
                <ActionList.LeadingVisual>
                  <PencilIcon />
                </ActionList.LeadingVisual>
                Edit
              </ActionList.Item>
            )}
            {userCanPin && (
              <ActionList.Item onSelect={handlePin}>
                <ActionList.LeadingVisual>
                  <PinIcon />
                </ActionList.LeadingVisual>
                Pin
              </ActionList.Item>
            )}
            {userCanUnpin && (
              <ActionList.Item onSelect={handleUnpin}>
                <ActionList.LeadingVisual>
                  <PinSlashIcon />
                </ActionList.LeadingVisual>
                Unpin
              </ActionList.Item>
            )}
            {comment.viewerCanMinimize && !commentHidden && (
              <ActionMenu>
                <ActionMenu.Anchor>
                  <ActionList.Item>
                    <ActionList.LeadingVisual>
                      <EyeClosedIcon />
                    </ActionList.LeadingVisual>
                    Hide
                  </ActionList.Item>
                </ActionMenu.Anchor>

                <ActionMenu.Overlay>
                  <ActionList>
                    <HideCommentActionItems onSelect={hideComment} setMenuOpen={setMenuOpened} />
                  </ActionList>
                </ActionMenu.Overlay>
              </ActionMenu>
            )}
            {comment.viewerCanUnminimize && commentHidden && (
              <ActionList.Item onSelect={unhideComment}>
                <ActionList.LeadingVisual>
                  <EyeIcon />
                </ActionList.LeadingVisual>{' '}
                Unhide
              </ActionList.Item>
            )}
            {comment.viewerCanDelete && (
              <ActionList.Item variant="danger" onSelect={deleteComment}>
                <ActionList.LeadingVisual>
                  <TrashIcon />
                </ActionList.LeadingVisual>
                Delete
              </ActionList.Item>
            )}
            {showReportSection && (
              <>
                <ActionList.Divider />
                {comment.viewerCanReportToMaintainer ? (
                  <ActionList.Item
                    onSelect={() => {
                      setIsReportContentOpen(true)
                    }}
                  >
                    <ActionList.LeadingVisual>
                      <ReportIcon />
                    </ActionList.LeadingVisual>
                    Report content
                  </ActionList.Item>
                ) : (
                  <ActionList.LinkItem href={reportUrl} target="_blank">
                    <ActionList.LeadingVisual>
                      <ReportIcon />
                    </ActionList.LeadingVisual>
                    Report content
                  </ActionList.LinkItem>
                )}
              </>
            )}
            {/* TODO view in stafftools */}
            {(userCanBlock || userCanUnblock) && !showReportSection && <ActionList.Divider />}
            {userCanBlock && (
              <ActionList.Item onSelect={() => setIsBlockUserFromOrgOpen(true)}>
                <ActionList.LeadingVisual>
                  <BlockedIcon />
                </ActionList.LeadingVisual>
                Block user
              </ActionList.Item>
            )}
            {userCanUnblock && (
              <ActionList.Item onSelect={() => setIsUnblockUserFromOrgOpen(true)}>
                <ActionList.LeadingVisual>
                  <BlockedIcon />
                </ActionList.LeadingVisual>
                Unblock user
              </ActionList.Item>
            )}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    </>
  )
}
